import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import WorkOrderListModal from '../components/WorkOrderListModal';
import { moneyRound } from '../lib/money';
import './AdminAnalyticsDashboard.css';

/** The window each metric covers, so a drill-down list matches its tile. */
const RANGE_DAYS = { '7d': 7, '30d': 30, '90d': 90, all: null };

const dateFrom = (timeRange) => {
  const days = RANGE_DAYS[timeRange];
  if (!days) return null;
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const RANGE_LABEL = {
  '7d': 'last 7 days', '30d': 'last 30 days', '90d': 'last 90 days', all: 'all time'
};

export default function AdminAnalyticsDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  // A failed read used to leave this page showing its empty state, which reads
  // as "no work orders" rather than "this did not load".
  const [error, setError] = useState(null);
  const [drilldown, setDrilldown] = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // No Authorization header is built here. lib/apiClient patches axios and
      // fetch and attaches the real staff token to every call to this API, and
      // it treats a hand-built `Bearer null` as not-a-credential precisely
      // because several pages used to send one. The token this page read has
      // not been written by anything since staff sign-in moved to Entra.
      const [analyticsRes, contractorsRes] = await Promise.all([
        axios.get(`${API_BASE}/analytics/work-orders?timeRange=${timeRange}`),
        axios.get(`${API_BASE}/contractors/performance`)
      ]);

      setAnalytics(analyticsRes.data || {});
      setContractors(contractorsRes.data || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // This used to removeItem('token') on a 401 - a key nothing writes,
      // so it removed nothing and only looked like sign-out handling. A real
      // 401 here means the Entra session has gone, which apiClient and the
      // sign-in flow own; this page just reports that it could not load.
      setError(error.response?.data?.error || 'Could not load the analytics');
    } finally {
      setLoading(false);
    }
  }, [timeRange, API_BASE]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  // The numbers come from the analytics endpoint; the list behind a number comes
  // from the work-order endpoint with the same status and window, so the two
  // cannot disagree about what a tile counts.
  const openDrilldown = async ({ title, status }) => {
    setDrilldown({ title, status, loading: true, workOrders: [], error: null });
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (status) params.set('status', status);
      const from = dateFrom(timeRange);
      if (from) params.set('date_from', from);

      const res = await axios.get(`${API_BASE}/work-orders?${params}`, {
      });
      const payload = res.data || {};
      setDrilldown((d) => (d && d.title === title
        ? { ...d, loading: false, workOrders: payload.workOrders || [] }
        : d));
    } catch (err) {
      console.error('Error loading work order drill-down:', err);
      setDrilldown((d) => (d && d.title === title
        ? { ...d, loading: false, error: 'Could not load these work orders.' }
        : d));
    }
  };

  if (loading) return <div className="analytics-container loading">Loading analytics...</div>;

  return (
    <div className="analytics-container">
      {error && <p className="an-error" role="alert">{error}</p>}
      <header className="analytics-header">
        <h1>Admin Analytics Dashboard</h1>
        <div className="header-controls">
          <select
            className="time-range-select"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
          <button className="refresh-btn" onClick={fetchAnalytics}>↻ Refresh</button>
        </div>
      </header>

      {analytics && (
        <>
          {/* Key Metrics */}
          <div className="metrics-section">
            <h2>Key Metrics</h2>
            <div className="metrics-grid">
              <button
                type="button"
                className="metric-card primary metric-card-clickable"
                onClick={() => openDrilldown({ title: 'Total Work Orders', status: null })}
                title="Show these work orders"
              >
                <div className="metric-icon">📋</div>
                <div className="metric-content">
                  <div className="metric-value">{analytics.total_work_orders || 0}</div>
                  <div className="metric-label">Total Work Orders</div>
                  <div className="metric-hint">View work orders</div>
                </div>
              </button>

              <button
                type="button"
                className="metric-card success metric-card-clickable"
                onClick={() => openDrilldown({ title: 'Completed', status: 'completed' })}
                title="Show these work orders"
              >
                <div className="metric-icon">✓</div>
                <div className="metric-content">
                  <div className="metric-value">{analytics.completed_work_orders || 0}</div>
                  <div className="metric-label">Completed</div>
                  <div className="metric-hint">View work orders</div>
                </div>
              </button>

              <button
                type="button"
                className="metric-card warning metric-card-clickable"
                onClick={() => openDrilldown({ title: 'In Progress', status: 'in_progress' })}
                title="Show these work orders"
              >
                <div className="metric-icon">⏳</div>
                <div className="metric-content">
                  <div className="metric-value">{analytics.in_progress_work_orders || 0}</div>
                  <div className="metric-label">In Progress</div>
                  <div className="metric-hint">View work orders</div>
                </div>
              </button>

              <div className="metric-card info">
                <div className="metric-icon">⌚</div>
                <div className="metric-content">
                  <div className="metric-value">{analytics.total_hours_logged?.toFixed(1) || 0}</div>
                  <div className="metric-label">Total Hours Logged</div>
                </div>
              </div>

              <div className="metric-card success">
                <div className="metric-icon">🎯</div>
                <div className="metric-content">
                  <div className="metric-value">{((analytics.on_time_completion_rate || 0) * 100).toFixed(0)}%</div>
                  <div className="metric-label">On-Time Completion</div>
                </div>
              </div>

              <div className="metric-card primary">
                <div className="metric-icon">💰</div>
                <div className="metric-content">
                  {/* This figure is hours logged by contractors priced at
                      their own rate, which is what NCMB pays out - not what it
                      takes in. Calling it revenue put the sign of the whole
                      number backwards on the one tile people actually read.
                      It was also the last place in the app still quoting
                      dollars. */}
                  <div className="metric-value">
                    {moneyRound(analytics.estimated_expense ?? analytics.estimated_revenue)}
                  </div>
                  <div className="metric-label">Estimated Expense</div>
                </div>
              </div>
            </div>
          </div>

          {/* Work Order Status Distribution */}
          <div className="status-section">
            <h2>Work Order Status Distribution</h2>
            <div className="status-bars">
              <button
                type="button"
                className="status-bar-item status-bar-clickable"
                onClick={() => openDrilldown({ title: 'Draft work orders', status: 'draft' })}
                title="Show the draft work orders"
              >
                <span className="status-bar-label">Draft</span>
                <span className="bar-container">
                  <span className="bar-fill draft" style={{ width: `${Math.max((analytics.status_draft_percent || 0) * 100, (analytics.status_draft || 0) > 0 ? 6 : 0)}%` }}>
                    <span>{analytics.status_draft || 0}</span>
                  </span>
                </span>
                <span className="percentage">{((analytics.status_draft_percent || 0) * 100).toFixed(0)}%</span>
              </button>

              <button
                type="button"
                className="status-bar-item status-bar-clickable"
                onClick={() => openDrilldown({ title: 'Issued work orders', status: 'issued' })}
                title="Show the issued work orders"
              >
                <span className="status-bar-label">Issued</span>
                <span className="bar-container">
                  <span className="bar-fill issued" style={{ width: `${Math.max((analytics.status_issued_percent || 0) * 100, (analytics.status_issued || 0) > 0 ? 6 : 0)}%` }}>
                    <span>{analytics.status_issued || 0}</span>
                  </span>
                </span>
                <span className="percentage">{((analytics.status_issued_percent || 0) * 100).toFixed(0)}%</span>
              </button>

              <button
                type="button"
                className="status-bar-item status-bar-clickable"
                onClick={() => openDrilldown({ title: 'In Progress work orders', status: 'in_progress' })}
                title="Show the in progress work orders"
              >
                <span className="status-bar-label">In Progress</span>
                <span className="bar-container">
                  <span className="bar-fill in-progress" style={{ width: `${Math.max((analytics.status_in_progress_percent || 0) * 100, (analytics.status_in_progress || 0) > 0 ? 6 : 0)}%` }}>
                    <span>{analytics.status_in_progress || 0}</span>
                  </span>
                </span>
                <span className="percentage">{((analytics.status_in_progress_percent || 0) * 100).toFixed(0)}%</span>
              </button>

              <button
                type="button"
                className="status-bar-item status-bar-clickable"
                onClick={() => openDrilldown({ title: 'Completed work orders', status: 'completed' })}
                title="Show the completed work orders"
              >
                <span className="status-bar-label">Completed</span>
                <span className="bar-container">
                  <span className="bar-fill completed" style={{ width: `${Math.max((analytics.status_completed_percent || 0) * 100, (analytics.status_completed || 0) > 0 ? 6 : 0)}%` }}>
                    <span>{analytics.status_completed || 0}</span>
                  </span>
                </span>
                <span className="percentage">{((analytics.status_completed_percent || 0) * 100).toFixed(0)}%</span>
              </button>
            </div>
          </div>

          {/* Contractor Performance */}
          {contractors.length > 0 && (
            <div className="contractors-section">
              <h2>Contractor Performance</h2>
              <div className="contractors-table">
                <div className="table-header">
                  <div className="col-name">Contractor</div>
                  <div className="col-orders">Work Orders</div>
                  <div className="col-completed">Completed</div>
                  <div className="col-hours">Hours Logged</div>
                  <div className="col-rating">On-Time Rate</div>
                </div>

                {contractors.map(contractor => (
                  <div key={contractor.id} className="table-row">
                    <div className="col-name">
                      <div className="contractor-name">{contractor.name}</div>
                      <div className="contractor-email">{contractor.email}</div>
                    </div>
                    <div className="col-orders">{contractor.total_work_orders || 0}</div>
                    <div className="col-completed">{contractor.completed_work_orders || 0}</div>
                    <div className="col-hours">{contractor.total_hours_logged?.toFixed(1) || 0}h</div>
                    <div className="col-rating">
                      <span className={`rating ${contractor.on_time_completion_rate >= 0.9 ? 'high' : contractor.on_time_completion_rate >= 0.7 ? 'medium' : 'low'}`}>
                        {((contractor.on_time_completion_rate || 0) * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hours Trends */}
          <div className="trends-section">
            <h2>Hours & Allocation Trends</h2>
            <div className="trends-grid">
              <div className="trend-card">
                <div className="trend-label">Average Hours Allocated</div>
                <div className="trend-value">{analytics.avg_hours_allocated?.toFixed(1) || 0}h</div>
                <div className="trend-change">Per work order</div>
              </div>

              <div className="trend-card">
                <div className="trend-label">Average Hours Logged</div>
                <div className="trend-value">{analytics.avg_hours_logged?.toFixed(1) || 0}h</div>
                <div className="trend-change">Per work order</div>
              </div>

              <div className="trend-card">
                <div className="trend-label">Hour Variance</div>
                <div className={`trend-value ${(analytics.avg_variance || 0) > 0 ? 'positive' : 'negative'}`}>
                  {(analytics.avg_variance || 0) > 0 ? '+' : ''}{analytics.avg_variance?.toFixed(1) || 0}h
                </div>
                <div className="trend-change">{(analytics.avg_variance || 0) > 0 ? 'Under budget' : 'Over budget'}</div>
              </div>

              <div className="trend-card">
                <div className="trend-label">Utilization Rate</div>
                <div className="trend-value">{((analytics.utilization_rate || 0) * 100).toFixed(0)}%</div>
                <div className="trend-change">Contractor capacity</div>
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="summary-section">
            <h2>Summary</h2>
            <div className="summary-stats">
              <div className="stat">
                <span className="stat-label">Average Time to Complete:</span>
                <span className="stat-value">{analytics.avg_completion_time_hours?.toFixed(1) || 0} hours</span>
              </div>
              <div className="stat">
                <span className="stat-label">SLA Compliance:</span>
                <span className="stat-value">{((analytics.sla_compliance_rate || 0) * 100).toFixed(0)}%</span>
              </div>
              <div className="stat">
                <span className="stat-label">Most Active Contractor:</span>
                <span className="stat-value">{analytics.top_contractor_name || 'N/A'}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {drilldown && (
        <WorkOrderListModal
          title={drilldown.title}
          subtitle={RANGE_LABEL[timeRange]}
          workOrders={drilldown.workOrders}
          loading={drilldown.loading}
          error={drilldown.error}
          onClose={() => setDrilldown(null)}
        />
      )}
    </div>
  );
}
